/**
 * Фича «Поставить объект из каталога».
 *
 * Что ставим — выбирается в панели каталога (`editor.placing`). Куда ставится,
 * решает поле `attach` у типа в каталоге:
 *   • `onlyWall` — на грань стены: кликом по стене на плане или по развёртке.
 *     На плане высоты нет, поэтому берётся типовая отметка типа, а грань
 *     определяется тем, с какой стороны от оси пришёлся клик;
 *   • `everywhere` — на пол: кликом в любое место плана, центром в точку;
 *   • `ceiling` — на потолок: так же кликом в любое место плана, но объект уходит
 *     в слой электрики как светильник — под потолок его поднимают движки.
 *
 * Перетаскивание объектов на стене работает в координатах развёртки (x — вдоль
 * стены, y — от пола), а перевод экрана в эти координаты даёт виджет: только он
 * знает, как разложены листы развёрток.
 *
 * Привязки вдоль стены и по высоте — общие с 3D, в `logic.ts`. Alt отключает привязки.
 *
 * Поставили один объект — инструмент возвращается в «Выбор», а новый объект остаётся
 * выделенным: следующий клик по нему его двигает, а не ставит ещё один. Иначе на планшете,
 * где нет Escape, из режима установки не выйти. Следующий такой же берётся из каталога.
 *
 * Пока тип выбран, за курсором по холсту едет призрак предмета (`ghost`): холст сообщает
 * положение курсора через `trackCursor`, а хук считает, где предмет встанет по клику, —
 * той же математикой, что и сама установка. Прибор «на стену» прилипает к ближайшей стене.
 * Карточку, перетащенную из каталога, холст ставит через `dropAt` — ровно туда, где призрак.
 * Так же и клик рядом со стеной: прибор «на стену» встаёт туда, где призрак, а не только
 * по клику точно в тело стены. Далеко от стен — подсказка `hint`.
 */
import { useRef, useState } from "react";
import type { PointerEvent } from "react";
import { detectRooms, offsetFromElevationX, type WallElevation } from "@/widgets/plan";
import { SelectionKind, Tool, type Selection, useEditorStore } from "@/widgets/editor";
import {
	floorItemsStoreFor,
	useBaseboardStore,
	useCeilingStore,
	useFixturesStore,
	useFloorsStore,
	useScreedStore,
	useWaterproofStore,
} from "@/shared/store/layers";
import { useProjectStore } from "@/shared/store";
import {
	catalogSectionUsesLayer,
	CatalogSectionKind,
	LayerKind,
	layerKindOfCatalogSection,
	ROOM_LAYERS,
} from "@/shared/plan";
import {
	dist,
	leftNormal,
	norm,
	projectOnSegment,
	snapTo,
	sub,
	type Pt,
	pointInPolygon,
} from "@/shared/lib/geometry";
import {
	baseboardItemById,
	CatalogAttach,
	ceilingItemById,
	floorItemById,
	getCatalogItem,
	getFloorPreset,
	screedItemById,
	waterproofItemById,
} from "@/shared/catalog";
import type { PlacementGhost } from "../types";
import { addWallFixture, setWallFixturePlacement, snapAlongWall, snapHeight } from "../logic";

/** Ближе этого расстояния до оси стены призрак прибора «на стену» прилипает к ней, мм. */
const WALL_SNAP = 450;

/** Id призрака — в слои он не попадает, только рисуется. */
const GHOST_ID = "ghost";

/** Точка развёртки в её собственных координатах. */
export interface ElevationPoint {
	x: number;
	y: number;
}

/** Что нужно знать о листе развёртки, чтобы двигать по нему объект. */
export interface FixtureDragTarget {
	elevation: WallElevation;
	/** Экран → координаты этой развёртки */
	toLocal: (clientX: number, clientY: number) => ElevationPoint;
}

/**
 * Захват указателя: без него перетаскивание срывается, стоит курсору
 * выйти за значок. Бросает исключение, если указатель уже «отпущен» системой,
 * — тогда просто продолжаем без захвата, жест от этого не ломается.
 */
function capture(element: Element, pointerId: number) {
	try {
		element.setPointerCapture?.(pointerId);
	} catch {
		// указателя уже нет — не страшно
	}
}

export const usePlaceFixture = () => {
	const tool = useEditorStore((s) => s.tool);
	const placingRef = useEditorStore((s) => s.placing);
	const gridStep = useEditorStore((s) => s.gridStep);
	const setSelection = useEditorStore((s) => s.setSelection);
	const setTool = useEditorStore((s) => s.setTool);
	const pushHistory = useProjectStore((s) => s.pushHistory);
	const addCeilingItem = useFixturesStore((s) => s.addCeilingItem);
	const setRoomBaseboardMaterial = useBaseboardStore((s) => s.setRoomBaseboardMaterial);
	const setRoomFloorMaterial = useFloorsStore((s) => s.setRoomFloorMaterial);
	const setRoomWaterproofMaterial = useWaterproofStore((s) => s.setRoomWaterproofMaterial);
	const setRoomScreedMaterial = useScreedStore((s) => s.setRoomScreedMaterial);
	const setRoomCeilingMaterial = useCeilingStore((s) => s.setRoomCeilingMaterial);
	const graph = useProjectStore((s) => s.project.layers.walls);

	/** Объект поставлен: выделяем его и возвращаемся к инструменту «Выбор» */
	const placed = (selection: Selection) => {
		setSelection(selection);
		setTool(Tool.Select);
	};

	const drag = useRef<{ id: string; target: FixtureDragTarget } | null>(null);
	const [dragging, setDragging] = useState(false);
	/** Курсор над холстом в мм и зажат ли Alt (без привязок); null — курсор ушёл с холста */
	const [cursor, setCursor] = useState<{ point: Pt; free: boolean } | null>(null);

	// Пол, гидроизоляция, стяжка и отделка стен назначаются комнате, а не создаются как объекты
	const isRoomAssignment =
		placingRef !== null &&
		(ROOM_LAYERS.includes(layerKindOfCatalogSection(placingRef.catalog)) ||
			catalogSectionUsesLayer(placingRef.catalog, LayerKind.WallFinish));
	const placing = tool === Tool.Place && placingRef !== null && !isRoomAssignment;
	const info = placingRef ? getCatalogItem(placingRef) : null;
	/** Выбранный объект ставится на стену, а не на пол */
	const onWall = info?.attach === CatalogAttach.OnlyWall;
	/** Выбранный объект — светильник на потолок */
	const onCeiling = info?.attach === CatalogAttach.Ceiling;

	/** Точка на полу с привязкой к сетке — куда встанет предмет по клику */
	const floorPoint = (world: Pt, free: boolean): Pt =>
		free ? world : { x: snapTo(world.x, gridStep), y: snapTo(world.y, gridStep) };

	/**
	 * Положение прибора на стене по точке плана: смещение вдоль оси с шагом привязки
	 * и грань — та, с которой пришлась точка. null — стены нет или она слишком короткая.
	 */
	const wallPlacementAt = (wallId: string, world: Pt): { offset: number; side: 1 | -1 } | null => {
		const wall = graph.walls[wallId];
		if (!wall) return null;
		const a = graph.nodes[wall.a];
		const b = graph.nodes[wall.b];
		if (!a || !b) return null;
		const length = dist(a, b);
		if (length < 100) return null;

		const projection = projectOnSegment(world, a, b);
		const along = norm(sub(b, a));
		const normal = leftNormal(along);
		const away = sub(world, projection.point);
		const side: 1 | -1 = away.x * normal.x + away.y * normal.y >= 0 ? 1 : -1;
		const offset = snapAlongWall(projection.t * length, false);
		return { offset, side };
	};

	/** Ближайшая к точке стена, если она в радиусе прилипания */
	const nearestWall = (world: Pt): string | null => {
		let best: string | null = null;
		let bestDistance = WALL_SNAP;
		for (const wall of Object.values(graph.walls)) {
			const a = graph.nodes[wall.a];
			const b = graph.nodes[wall.b];
			if (!a || !b) continue;
			const { distance } = projectOnSegment(world, a, b);
			if (distance < bestDistance) {
				bestDistance = distance;
				best = wall.id;
			}
		}
		return best;
	};

	/** Холст сообщает, где курсор; null — курсор ушёл с холста */
	const trackCursor = (point: Pt | null, free = false) => setCursor(point ? { point, free } : null);

	/** Призрак предмета под курсором — см. PlacementGhost */
	const ghost = ((): PlacementGhost | null => {
		if (!placing || !placingRef || !info || !cursor) return null;
		const { point, free } = cursor;
		if (!onWall) {
			const at = floorPoint(point, free);
			const item = { id: GHOST_ID, ref: placingRef, x: at.x, y: at.y, rotation: 0 };
			return { kind: onCeiling ? "ceiling" : "floor", item };
		}
		const wallId = nearestWall(point);
		const at = wallId ? wallPlacementAt(wallId, point) : null;
		if (!wallId || !at) return { kind: "free", ref: placingRef, point };
		const fixture = {
			id: GHOST_ID,
			wallId,
			ref: placingRef,
			offset: at.offset,
			elevation: info.elevation,
			side: at.side,
		};
		return { kind: "wall", fixture };
	})();

	/**
	 * Подсказка у курсора: прибор «на стену» увели от стен — непонятно, куда кликать.
	 * Рядом со стеной призрак сам прилипает к ней, и клик ставит его туда.
	 */
	const hint =
		ghost?.kind === "free" && info
			? `${info.name} ставится на стену: подведите курсор ближе к стене и кликните`
			: null;

	/**
	 * Клик по свободному месту плана: объект «на пол» или «на потолок» встаёт
	 * центром в точку с привязкой к сетке. Объект «на стену» так поставить
	 * нельзя — false, и холст покажет подсказку.
	 */
	const placeOnFloor = (world: Pt, free: boolean): boolean => {
		if (!placing || !placingRef || !info || onWall) return false;
		const { x, y } = floorPoint(world, free);
		if (onCeiling) {
			const id = addCeilingItem(placingRef, x, y);
			placed({ kind: SelectionKind.Light, id });
			return true;
		}
		// Слой выбирает раздел каталога: унитаз — в сантехнику, кровать — в мебель
		const id = floorItemsStoreFor(placingRef.catalog).getState().addFloorItem(placingRef, x, y);
		placed({ kind: SelectionKind.Item, id });
		return true;
	};

	/** Клик по развёртке: ставит объект выбранного типа на эту стену. */
	const placeOnElevation = (
		elevation: WallElevation,
		point: ElevationPoint,
		free: boolean,
	): boolean => {
		if (!placing || !placingRef || !info || !onWall) return false;
		const offset = offsetFromElevationX(elevation, snapAlongWall(point.x, free));
		// По вертикали объект всегда встаёт на типовую отметку своего типа:
		// мышью выбирают место вдоль стены, а высота у розеток нормируется.
		// Поднять или опустить его можно потом — перетаскиванием или числом.
		const id = addWallFixture(elevation.wallId, placingRef, offset, info.elevation, elevation.side);
		if (id) placed({ kind: SelectionKind.Fixture, id });
		return true;
	};

	/**
	 * Клик по стене на плане. Сторона стены — та, с которой пришёлся клик:
	 * розетка должна оказаться на той грани, куда пользователь ткнул.
	 * Объект «на пол» кликом по стене тоже ставится — на пол в этой точке.
	 */
	const placeOnPlan = (wallId: string, world: Pt): boolean => {
		if (!placing || !placingRef || !info) return false;
		if (!onWall) return placeOnFloor(world, false);

		const at = wallPlacementAt(wallId, world);
		if (!at) return false;
		const id = addWallFixture(wallId, placingRef, at.offset, info.elevation, at.side);
		if (id) placed({ kind: SelectionKind.Fixture, id });
		return true;
	};

	/**
	 * Карточку из каталога отпустили над холстом: объект встаёт туда, где был призрак, —
	 * прибор «на стену» на ближайшую стену, остальное в точку. false — ставить некуда
	 * (розетку отпустили далеко от стен).
	 */
	const dropAt = (world: Pt, free: boolean): boolean => {
		const roomAssignment = placingRef;
		if (
			roomAssignment?.catalog === CatalogSectionKind.Floor ||
			roomAssignment?.catalog === CatalogSectionKind.Waterproof ||
			roomAssignment?.catalog === CatalogSectionKind.Screed ||
			roomAssignment?.catalog === CatalogSectionKind.Ceiling ||
			roomAssignment?.catalog === CatalogSectionKind.Baseboard
		) {
			const room = detectRooms(graph).find((candidate) => pointInPolygon(world, candidate.inner));
			if (!room) return false;
			pushHistory();
			const assigned = (() => {
				switch (roomAssignment.catalog) {
					case CatalogSectionKind.Floor: {
						const item = floorItemById(roomAssignment.item);
						if (!item) return false;
						const preset = getFloorPreset(item.material);
						setRoomFloorMaterial(room.id, item.material, preset.layout, preset);
						return true;
					}
					case CatalogSectionKind.Waterproof: {
						const item = waterproofItemById(roomAssignment.item);
						if (!item) return false;
						setRoomWaterproofMaterial(room.id, item.material);
						return true;
					}
					case CatalogSectionKind.Screed: {
						const item = screedItemById(roomAssignment.item);
						if (!item) return false;
						setRoomScreedMaterial(room.id, item.material);
						return true;
					}
					case CatalogSectionKind.Ceiling: {
						const item = ceilingItemById(roomAssignment.item);
						if (!item) return false;
						setRoomCeilingMaterial(room.id, item.material);
						return true;
					}
					case CatalogSectionKind.Baseboard: {
						const item = baseboardItemById(roomAssignment.item);
						if (!item) return false;
						setRoomBaseboardMaterial(room.id, item.material);
						return true;
					}
				}
			})();
			if (!assigned) return false;
			placed(
				roomAssignment.catalog === CatalogSectionKind.Waterproof ||
					roomAssignment.catalog === CatalogSectionKind.Screed
					? null
					: { kind: SelectionKind.Room, id: room.id },
			);
			return true;
		}
		if (!placing || !placingRef || !info) return false;
		if (onWall) {
			const wallId = nearestWall(world);
			return wallId !== null && placeOnPlan(wallId, world);
		}
		pushHistory();
		return placeOnFloor(world, free);
	};

	const beginDrag = (e: PointerEvent<SVGGElement>, id: string, target: FixtureDragTarget) => {
		if (e.button !== 0) return;
		e.stopPropagation();
		capture(e.currentTarget, e.pointerId);
		pushHistory();
		drag.current = { id, target };
		setDragging(true);
	};

	const handlePointerMove = (e: PointerEvent<SVGElement>): boolean => {
		const current = drag.current;
		if (!current) return false;
		const point = current.target.toLocal(e.clientX, e.clientY);
		const free = e.altKey;
		setWallFixturePlacement(
			current.id,
			offsetFromElevationX(current.target.elevation, snapAlongWall(point.x, free)),
			snapHeight(point.y, free),
		);
		return true;
	};

	const handlePointerUp = () => {
		if (!drag.current) return;
		drag.current = null;
		setDragging(false);
	};

	return {
		placing,
		/** Назначаем комнате пол, потолок или отделку: клик по мебели должен уйти комнате */
		assigningRoom: tool === Tool.Place && isRoomAssignment,
		/** Тип, который ставится следующим; null — ничего не выбрано или тип удалён из каталога */
		info,
		onWall,
		onCeiling,
		dragging,
		ghost,
		hint,
		trackCursor,
		placeOnElevation,
		placeOnPlan,
		placeOnFloor,
		dropAt,
		beginDrag,
		handlePointerMove,
		handlePointerUp,
	};
};
